import React, { useState, useEffect } from 'react';
import { createSubAdmin, getSubAdmins, deleteSubAdmin } from 'utils/adminApi';
import Toast from 'utils';
import styles from './subadmin.module.css';
import PopUpModal from 'common/Popup';
import Button from 'common/Button';
import Badge from 'common/Badge';
import DataTable_ from "common/DataTable";

const SubAdminManager = ({ adminId, authToken }) => {
  const [subAdmins, setSubAdmins] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [subAdminToDelete, setSubAdminToDelete] = useState(null);

  useEffect(() => {
    fetchSubAdmins();
  }, []);

  const fetchSubAdmins = async () => {
    try {
      setLoading(true);
      const response = await getSubAdmins(adminId, authToken);
      setSubAdmins(response.data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching sub-admins:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      await createSubAdmin(adminId, email, authToken);
      Toast().Success('Sub-admin invite sent successfully!');
      setSuccess('Sub-admin invite sent successfully!');
      setEmail('');
      fetchSubAdmins();
    } catch (err) {
      console.error('Error creating sub-admin:', err);
      Toast().Error(err.message || 'Failed to create sub-admin');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (subAdmin) => {
    setSubAdminToDelete(subAdmin);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!subAdminToDelete) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await deleteSubAdmin(adminId, subAdminToDelete.id, authToken);
      Toast().Success('Sub-admin access revoked successfully');
      setSuccess('Sub-admin access revoked successfully');
      fetchSubAdmins();
    } catch (err) {
      console.error('Error deleting sub-admin:', err);
      Toast().Error(err.message || 'Failed to delete sub-admin');
      setError(err.message);
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
      setSubAdminToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSubAdminToDelete(null);
  };
    
  const columns = [
    {
      name: "Email",
      selector: (row) => row.email,
    },
    {
      name: "Name",
      selector: (row) => row.first_name || '-',
    },
    {
      name: "Added On",
      selector: (row) => new Date(row.created_at).toLocaleDateString(),
    },
    {
      name: "Status",
      cell: (row) => (
        <span className={`${styles.status} ${row.is_verified ? styles.verified : styles.pending}`}>
          {row.is_verified ? 'Active' : 'Pending'}
        </span>
      ),
    },
    {
      name: "Action",
      cell: (row) => (
        <Badge
          label="Revoke Access"
          onClick={() => handleDeleteClick(row)}
          style={{ 
            backgroundColor: "#E34A4A", 
            color: "white",
            cursor: "pointer",
            width: "max-content"
          }}
        />
      ),
    },
  ];

  return (
    <div className={styles.subAdminManager}>
      <h3 className={styles.sectionTitle}>Sub-Admin Management</h3>
      <p className={styles.description}>
        Add team members who can access and manage your account
      </p>

      {/* Add Sub-Admin Form */}
      <form onSubmit={handleCreateSubAdmin} className={styles.addSubAdminForm}>
        <div className={styles.formGroup}>
          <label htmlFor="subAdminEmail">Email Address</label>
          <div className={styles.inputGroup}>
            <input
              id="subAdminEmail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email address"
              disabled={loading}
              required
            />
            <button type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </div>
      </form>

      {/* Success/Error Messages */}
      {error && <div className={`${styles.alert} ${styles.alertError}`}>{error}</div>}
      {success && <div className={`${styles.alert} ${styles.alertSuccess}`}>{success}</div>}

      {/* Sub-Admins List */}
      <div className={styles.subAdminsList}>
        <h3>Current Sub-Admins ({subAdmins.length})</h3>
        
        {loading && subAdmins.length === 0 ? (
          <p className={styles.loading}>Loading...</p>
        ) : (
          <DataTable_
            columns={columns}
            data={subAdmins}
            pagination
            customStyles={{
                table: {
                    style: {
                        width: '100%',
                    }
                }
            }}
          />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <PopUpModal modalIsOpen={deleteModalOpen} setModalIsOpen={setDeleteModalOpen}>
        <div className={styles.confirmModal}>
          <h3 className={styles.confirmTitle}>Revoke Sub-Admin Access</h3>
          <p className={styles.confirmMessage}>
            Are you sure you want to revoke access for{' '}
            <strong>{subAdminToDelete?.email}</strong>?
          </p>
          <p className={styles.confirmWarning}>
            This action cannot be undone. They will no longer be able to access the admin panel.
          </p>
          <div className={styles.confirmButtons}>
            <Button
              label="Cancel"
              onClick={handleCancelDelete}
              buttonOverrideStyle={styles.cancelButton}
            />
            <Button
              label="Revoke Access"
              onClick={handleConfirmDelete}
              buttonOverrideStyle={styles.confirmButton}
            />
          </div>
        </div>
      </PopUpModal>
    </div>
  );
};

export default SubAdminManager;

